import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import {
  BadgeCheck,
  Store,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  FileText,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RefreshCw
} from 'lucide-react';

const VERIFICATION_STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
  under_review: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Under Review' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' }
};

const AdminVerification = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/brand-registry/admin/verification-requests?';
      if (statusFilter && statusFilter !== 'all') {
        url += `status=${statusFilter}&`;
      }
      if (typeFilter && typeFilter !== 'all') {
        url += `verification_type=${typeFilter}&`;
      }
      
      const data = await apiCall(url);
      setRequests(data.requests || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch verification requests',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, statusFilter, typeFilter, toast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Open review modal
  const openReviewModal = (request, action) => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes('');
    setRejectionReason('');
    setReviewModalOpen(true);
  };

  // Submit review
  const handleReview = async () => {
    if (reviewAction === 'reject' && !rejectionReason) {
      toast({
        title: 'Required',
        description: 'Please provide a rejection reason',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      await apiCall(`/api/brand-registry/admin/verification-requests/${selectedRequest.id}/review`, {
        method: 'POST',
        body: JSON.stringify({
          action: reviewAction,
          notes: reviewNotes,
          rejection_reason: rejectionReason || null
        })
      });

      toast({
        title: reviewAction === 'approve' ? '✅ Approved' : '❌ Rejected',
        description: reviewAction === 'approve' 
          ? 'Verification approved. Badge has been granted.'
          : 'Verification rejected. Vendor has been notified.'
      });

      setReviewModalOpen(false);
      fetchRequests();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to process review',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = VERIFICATION_STATUS_CONFIG[status] || VERIFICATION_STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600" />
              Verification Requests
            </h1>
            <p className="text-muted-foreground mt-1">
              Review brand owner and legacy store verification requests
            </p>
          </div>
          <Button onClick={fetchRequests} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{total}</div>
            <div className="text-sm text-muted-foreground">Total Requests</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {requests.filter(r => r.verification_type === 'brand_owner').length}
            </div>
            <div className="text-sm text-muted-foreground">Blue Tick Requests</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {requests.filter(r => r.verification_type === 'legacy_store').length}
            </div>
            <div className="text-sm text-muted-foreground">Green Tick Requests</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-orange-600">
              {requests.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-muted-foreground">Pending Review</div>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4">
              <div className="w-48">
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label className="text-xs">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="brand_owner">Blue Tick (Brand Owner)</SelectItem>
                    <SelectItem value="legacy_store">Green Tick (Legacy Store)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No verification requests found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>
                        <div className="font-medium">{req.vendor_store_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{req.vendor_legal_name}</div>
                      </TableCell>
                      <TableCell>
                        {req.verification_type === 'brand_owner' ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            <BadgeCheck className="h-3 w-3 mr-1" />
                            Blue Tick
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">
                            <Store className="h-3 w-3 mr-1" />
                            Green Tick
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {req.verification_type === 'brand_owner' ? (
                          <div>
                            <div className="font-medium">{req.brand_name}</div>
                            <div className="text-xs text-muted-foreground">
                              TM# {req.tm_number} ({req.tm_status})
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-sm">{req.store_address?.slice(0, 30)}...</div>
                            <div className="text-xs text-muted-foreground">
                              Est. {req.store_established_year}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(req.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {/* View Documents */}
                          {req.tm_certificate_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const u = req.tm_certificate_url;
                                const base = process.env.REACT_APP_BACKEND_URL || '';
                                const resolved = u.startsWith('http') ? u : u.startsWith('/uploads/') ? `${base}/api${u}` : u.startsWith('/api/') ? `${base}${u}` : `${base}/api/uploads/kyc_documents/${u.replace(/^\//,'')}`;
                                window.open(resolved, '_blank');
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                          {req.shop_license_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const u = req.shop_license_url;
                                const base = process.env.REACT_APP_BACKEND_URL || '';
                                const resolved = u.startsWith('http') ? u : u.startsWith('/uploads/') ? `${base}/api${u}` : u.startsWith('/api/') ? `${base}${u}` : `${base}/api/uploads/kyc_documents/${u.replace(/^\//,'')}`;
                                window.open(resolved, '_blank');
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Review Actions */}
                          {(req.status === 'pending' || req.status === 'under_review') && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => openReviewModal(req, 'approve')}
                              >
                                <ThumbsUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => openReviewModal(req, 'reject')}
                              >
                                <ThumbsDown className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Review Modal */}
        <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {reviewAction === 'approve' ? (
                  <>
                    <ThumbsUp className="h-5 w-5 text-green-500" />
                    Approve Verification
                  </>
                ) : (
                  <>
                    <ThumbsDown className="h-5 w-5 text-red-500" />
                    Reject Verification
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {reviewAction === 'approve' 
                  ? 'This will grant the verification badge to the vendor.'
                  : 'Please provide a reason for rejection.'}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4">
                {/* Request Summary */}
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div><strong>Vendor:</strong> {selectedRequest.vendor_store_name}</div>
                  <div><strong>Type:</strong> {selectedRequest.verification_type === 'brand_owner' ? 'Blue Tick' : 'Green Tick'}</div>
                  {selectedRequest.brand_name && (
                    <div><strong>Brand:</strong> {selectedRequest.brand_name}</div>
                  )}
                </div>

                {/* Review Notes */}
                <div className="space-y-2">
                  <Label>Review Notes</Label>
                  <Textarea
                    placeholder="Optional notes for internal reference..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                </div>

                {/* Rejection Reason */}
                {reviewAction === 'reject' && (
                  <div className="space-y-2">
                    <Label>Rejection Reason *</Label>
                    <Textarea
                      placeholder="Explain why this request is being rejected..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      This will be shared with the vendor
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleReview}
                disabled={submitting}
                className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : reviewAction === 'approve' ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                {reviewAction === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminVerification;
